import React, { useState } from 'react';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { LOOTBOX_RARITIES, RARITY_TIERS_REVERSED, S2_MAX_TRADEABLE_RARITY, S2_TRADEUP_PROBABILITIES, getRarityTiers } from '../../constants/lootbox';
import { VFX_EFFECTS } from '../../constants/vfx';
import ItemIcon from './ItemIcon';
import { useToast } from '../ToastContext';

function TradeUpSection({ inventory, user, userData, onUserDataChange, isS2 }) {
  const toast = useToast();
  const [selectedItems, setSelectedItems] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);
  const [statTrakTooltip, setStatTrakTooltip] = useState(null); // { item, x, y }
  const [vfxTooltip, setVfxTooltip] = useState(null); // { item, x, y }

  // Group inventory by rarity (with optional duplicate filtering)
  const inventoryByRarity = React.useMemo(() => {
    let filteredInventory = inventory;

    // Apply duplicates filter if enabled
    // VFX, Shiny, and StatTrak variants are considered distinct items
    if (showDuplicatesOnly) {
      const itemKey = (i) => `${i.id}_${i.rarity}_${i.isStatTrak || false}_${i.isShiny || false}_${i.vfx || 'none'}`;
      const itemCounts = {};
      inventory.forEach(item => {
        const key = itemKey(item);
        itemCounts[key] = (itemCounts[key] || 0) + 1;
      });
      filteredInventory = inventory.filter(item => itemCounts[itemKey(item)] > 1);
    }

    return filteredInventory.reduce((acc, item) => {
      if (!acc[item.rarity]) {
        acc[item.rarity] = [];
      }
      acc[item.rarity].push(item);
      return acc;
    }, {});
  }, [inventory, showDuplicatesOnly]);

  function handleItemClick(item) {
    // Check if item is already selected
    const itemIndex = selectedItems.findIndex(
      selected => selected.unboxedAt === item.unboxedAt && selected.id === item.id
    );

    if (itemIndex >= 0) {
      // Deselect item
      setSelectedItems(selectedItems.filter((_, i) => i !== itemIndex));
    } else {
      // Determine max selection based on first selected item's rarity (or current item if none selected)
      const targetRarity = selectedItems.length > 0 ? selectedItems[0].rarity : item.rarity;
      const maxSelection = getRequiredTradeUpCount(targetRarity);

      // Only allow selection if under limit and same rarity
      if (selectedItems.length < maxSelection && (selectedItems.length === 0 || item.rarity === targetRarity)) {
        setSelectedItems([...selectedItems, item]);
      }
    }
  }

  function canTradeUp() {
    if (selectedItems.length === 0) return false;

    // All items must be same rarity
    const rarity = selectedItems[0].rarity;
    if (!selectedItems.every(item => item.rarity === rarity)) return false;

    if (isS2) {
      // S2: always 5 items, Legendary is max tradeable
      const maxTradeable = S2_MAX_TRADEABLE_RARITY || 'LEGENDARY';
      const tiers = getRarityTiers();
      const rarityIdx = tiers.indexOf(rarity);
      const maxIdx = tiers.indexOf(maxTradeable);
      if (rarityIdx < 0 || rarityIdx > maxIdx) return false;
      if (selectedItems.length !== 5) return false;
    } else {
      // S1: variable counts, cannot trade up from GALAXY or NUCLEAR
      if (rarity === 'GALAXY' || rarity === 'NUCLEAR') return false;
      const requiredCount = getRequiredTradeUpCount(rarity);
      if (selectedItems.length !== requiredCount) return false;
    }

    return true;
  }

  // Helper function to get required trade-up count
  function getRequiredTradeUpCount(rarity) {
    if (isS2) return 5; // S2: always 5
    if (rarity === 'EPIC') return 5;
    if (rarity === 'LEGENDARY') return 10;
    if (rarity === 'MYTHIC') return 15;
    return 5; // Default for COMMON, UNCOMMON, RARE
  }

  async function handleTradeUp() {
    if (!canTradeUp() || !user) return;
    if (isProcessing) return;
    setIsProcessing(true);

    // Capture items before clearing selection
    const itemsToSend = selectedItems.map(i => ({ id: i.id, unboxedAt: i.unboxedAt, rarity: i.rarity, isShiny: i.isShiny || false, isStatTrak: i.isStatTrak || false, vfx: i.vfx || [] }));
    setSelectedItems([]);
    setResult(null);

    try {
      const tradeUpFn = httpsCallable(functions, 'tradeUp');
      const serverResult = await tradeUpFn({
        selectedItems: itemsToSend
      });
      // Show the real server result — no optimistic guess that could mismatch
      setResult({ ...serverResult.data.item, outcomeType: serverResult.data.outcomeType });
      await onUserDataChange();
      setTimeout(() => setResult(null), 5000);
    } catch (error) {
      console.error("Error trading up:", error);
      setResult(null);
      toast.error(error.message || "Failed to trade up. Please try again.");
      await onUserDataChange(); // Refresh to restore items
    }

    setIsProcessing(false);
  }

  return (
    <div className="tradeup-container">
      <h2>⬆️ Trade Up Contract</h2>
      {isS2 ? (
        <>
          <p>Trade 5 items of the same rarity for a chance at higher tiers!</p>
          <p style={{ fontSize: '13px', color: 'var(--text-dim)', marginTop: '8px' }}>
            Outcomes: {S2_TRADEUP_PROBABILITIES.PLUS_1 * 100}% +1 rarity, {S2_TRADEUP_PROBABILITIES.SAME * 100}% same rarity (different item), {S2_TRADEUP_PROBABILITIES.PLUS_2 * 100}% +2 rarities, {S2_TRADEUP_PROBABILITIES.PLUS_3 * 100}% +3 rarities
          </p>
          <p style={{ fontSize: '13px', color: 'var(--text-dim)', marginTop: '4px' }}>
            Tradeable: Common through Legendary (5 items each)
          </p>
        </>
      ) : (
        <>
          <p>Trade items of the same rarity for 1 item of the next tier!</p>
          <p style={{ fontSize: '13px', color: 'var(--text-dim)', marginTop: '8px' }}>
            Required: 5 items (Common/Uncommon/Rare/Epic), 10 items (Legendary), or 15 items (Mythic)
          </p>
        </>
      )}

      {result && (
        <div className="tradeup-result" style={{
          marginTop: '20px',
          padding: '20px',
          background: `linear-gradient(135deg, ${LOOTBOX_RARITIES[result.rarity].color}22, transparent)`,
          border: `2px solid ${LOOTBOX_RARITIES[result.rarity].color}`,
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <h3 style={{ color: LOOTBOX_RARITIES[result.rarity].color, marginBottom: '12px' }}>
            {isS2 && result.outcomeType === 'same' ? '🔄 Same Rarity!' :
             isS2 && result.outcomeType === '+2' ? '🎉🎉 +2 Rarity Jump!' :
             isS2 && result.outcomeType === '+3' ? '🎉🎉🎉 +3 MEGA JUMP!' :
             '🎉 Trade Up Success!'}
          </h3>
          <div style={{ fontSize: '48px', margin: '12px 0' }}>
            <ItemIcon item={result} size={64} />
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: result.isShiny ? '#FFD700' : LOOTBOX_RARITIES[result.rarity].color
          }}>
            {result.isShiny && '✨ '}
            {result.name}
            {result.isShiny && ' ✨'}
          </div>
          <div style={{
            fontSize: '14px',
            color: LOOTBOX_RARITIES[result.rarity].color,
            marginTop: '8px'
          }}>
            {LOOTBOX_RARITIES[result.rarity].name}
          </div>
        </div>
      )}

      <div className="tradeup-selection" style={{ marginTop: '20px' }}>
        <h3>Selected Items ({selectedItems.length}/{selectedItems.length > 0 ? getRequiredTradeUpCount(selectedItems[0].rarity) : '?'})</h3>
        <div className="tradeup-selected-items" style={{
          display: 'flex',
          gap: '12px',
          marginTop: '12px',
          minHeight: '100px',
          padding: '16px',
          background: 'rgba(255, 255, 255, 0.02)',
          borderRadius: '8px',
          flexWrap: 'wrap'
        }}>
          {selectedItems.length === 0 ? (
            <p style={{ color: '#888', margin: 'auto' }}>Click items below to select them</p>
          ) : (
            selectedItems.map((item, index) => {
              const rarityData = LOOTBOX_RARITIES[item.rarity];
              return (
                <div
                  key={index}
                  className="tradeup-selected-item"
                  style={{
                    width: '80px',
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '32px',
                    background: `linear-gradient(135deg, ${rarityData.color}33, transparent)`,
                    border: `2px solid ${item.isShiny ? '#FFD700' : rarityData.color}`,
                    borderRadius: '8px',
                    position: 'relative',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleItemClick(item)}
                >
                  <ItemIcon item={item} size={32} />
                  {item.isShiny && (
                    <div style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      fontSize: '16px'
                    }}>
                      ✨
                    </div>
                  )}
                  {item.isStatTrak && (
                    <div style={{
                      position: 'absolute',
                      top: '2px',
                      left: '2px',
                      fontSize: '10px',
                      background: 'rgba(255, 140, 0, 0.9)',
                      padding: '2px 4px',
                      borderRadius: '3px',
                      fontWeight: 'bold',
                      color: '#000'
                    }}>
                      ST
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {selectedItems.length > 0 && (
          <div style={{ marginTop: '12px', fontSize: '14px', color: '#888' }}>
            {canTradeUp() ? (
              <>
                ✅ Ready to trade up!
                <div style={{ marginTop: '4px', fontSize: '12px' }}>
                  {selectedItems.filter(i => i.isShiny).length > 0 &&
                    `• Shiny chance: ${(selectedItems.filter(i => i.isShiny).length / selectedItems.length * 100).toFixed(0)}%`}
                  {selectedItems.filter(i => i.isShiny).length > 0 && selectedItems.filter(i => i.isStatTrak).length > 0 && <br />}
                  {selectedItems.filter(i => i.isStatTrak).length > 0 &&
                    `• StatTrak chance: ${(selectedItems.filter(i => i.isStatTrak).length / selectedItems.length * 100).toFixed(0)}%`}
                </div>
              </>
            ) : (isS2 && getRarityTiers().indexOf(selectedItems[0].rarity) > getRarityTiers().indexOf(S2_MAX_TRADEABLE_RARITY || 'LEGENDARY')) || selectedItems[0].rarity === 'GALAXY' || selectedItems[0].rarity === 'NUCLEAR' ? (
              '❌ Cannot trade up from this tier'
            ) : (
              `Select ${getRequiredTradeUpCount(selectedItems[0].rarity) - selectedItems.length} more ${selectedItems[0].rarity} items`
            )}
          </div>
        )}

        <button
          className="btn-primary"
          onClick={handleTradeUp}
          disabled={!canTradeUp() || isProcessing}
          style={{
            marginTop: '16px',
            width: '100%',
            padding: '12px',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          {isProcessing ? '⏳ Processing...' : '⬆️ Trade Up'}
        </button>
      </div>

      <div className="tradeup-inventory" style={{ marginTop: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0 }}>Your Inventory</h3>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            padding: '8px 12px',
            background: showDuplicatesOnly ? 'var(--primary-color)' : 'transparent',
            border: '1px solid var(--border-color)',
            borderRadius: '4px',
            transition: 'all 0.2s',
            userSelect: 'none'
          }}>
            <input
              type="checkbox"
              checked={showDuplicatesOnly}
              onChange={(e) => setShowDuplicatesOnly(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>Duplicates Only</span>
          </label>
        </div>
        {RARITY_TIERS_REVERSED.filter(rarity => {
          if (isS2) {
            // S2: only show Common through Legendary (tradeable)
            const tiers = getRarityTiers();
            const maxIdx = tiers.indexOf(S2_MAX_TRADEABLE_RARITY || 'LEGENDARY');
            return tiers.indexOf(rarity) >= 0 && tiers.indexOf(rarity) <= maxIdx && inventoryByRarity[rarity]?.length > 0;
          }
          return rarity !== 'GALAXY' && rarity !== 'NUCLEAR' && inventoryByRarity[rarity]?.length > 0;
        }).map(rarity => {
          const rarityData = LOOTBOX_RARITIES[rarity];
          const items = inventoryByRarity[rarity];

          return (
            <div key={rarity} className="tradeup-rarity-group" style={{ marginTop: '20px' }}>
              <h4 style={{ color: rarityData.color, marginBottom: '12px' }}>
                {rarityData.name} ({items.length} items)
              </h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                gap: '12px'
              }}>
                {items.map((item, index) => {
                  const isSelected = selectedItems.some(
                    selected => selected.unboxedAt === item.unboxedAt && selected.id === item.id
                  );

                  return (
                    <div
                      key={`${item.id}-${item.unboxedAt}-${index}`}
                      className="tradeup-item"
                      onClick={() => handleItemClick(item)}
                      onMouseEnter={(e) => {
                        if (item.isStatTrak) setStatTrakTooltip({ item, x: e.clientX, y: e.clientY });
                        if (item.vfx && item.vfx.length > 0) setVfxTooltip({ item, x: e.clientX, y: e.clientY });
                      }}
                      onMouseMove={(e) => {
                        if (item.isStatTrak) setStatTrakTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null);
                        if (item.vfx && item.vfx.length > 0) setVfxTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null);
                      }}
                      onMouseLeave={() => { setStatTrakTooltip(null); setVfxTooltip(null); }}
                      style={{
                        padding: '16px',
                        background: isSelected
                          ? `linear-gradient(135deg, ${rarityData.color}55, ${rarityData.color}22)`
                          : `linear-gradient(135deg, ${rarityData.color}33, transparent)`,
                        border: `2px solid ${isSelected ? rarityData.color : `${rarityData.color}88`}`,
                        borderRadius: '8px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        position: 'relative'
                      }}
                    >
                      {item.isStatTrak && (
                        <div style={{
                          position: 'absolute',
                          top: '4px',
                          left: '4px',
                          background: '#FF6B00',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          color: '#000'
                        }}>
                          ST
                        </div>
                      )}
                      <div style={{ marginBottom: '8px' }}>
                        <ItemIcon item={item} size={36} />
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: item.isShiny ? '#FFD700' : '#fff',
                        fontWeight: item.isShiny ? 'bold' : 'normal'
                      }}>
                        {item.name}
                      </div>
                      {item.isShiny && (
                        <div style={{
                          position: 'absolute',
                          top: '4px',
                          right: '4px',
                          fontSize: '20px'
                        }}>
                          ✨
                        </div>
                      )}
                      {item.vfx && item.vfx.length > 0 && (
                        <div style={{
                          position: 'absolute',
                          bottom: '4px',
                          right: '4px',
                          background: 'rgba(167,139,250,0.2)',
                          border: '1px solid #a78bfa',
                          borderRadius: '4px',
                          padding: '1px 5px',
                          fontSize: '9px',
                          fontWeight: 'bold',
                          color: '#a78bfa'
                        }}>
                          ✦{item.vfx.length > 1 ? ` VFX x${item.vfx.length}` : ' VFX'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {Object.keys(inventoryByRarity).filter(r => {
          if (isS2) {
            const tiers = getRarityTiers();
            const maxIdx = tiers.indexOf(S2_MAX_TRADEABLE_RARITY || 'LEGENDARY');
            return tiers.indexOf(r) >= 0 && tiers.indexOf(r) <= maxIdx;
          }
          return r !== 'GALAXY' && r !== 'NUCLEAR';
        }).length === 0 && (
          <p style={{ color: '#888', marginTop: '16px' }}>
            No items available for trade up. Open some lootboxes!
          </p>
        )}
      </div>

      {/* StatTrak hover tooltip */}
      {statTrakTooltip && (
        <div style={{
          position: 'fixed',
          left: statTrakTooltip.x + 14,
          top: statTrakTooltip.y + 14,
          zIndex: 9999,
          background: 'rgba(20,20,20,0.97)',
          border: '2px solid #FF8C00',
          borderRadius: '10px',
          padding: '12px 16px',
          pointerEvents: 'none',
          minWidth: '180px',
          boxShadow: '0 4px 24px rgba(255,140,0,0.3)'
        }}>
          <div style={{ color: '#FF8C00', fontWeight: 'bold', fontSize: '13px', marginBottom: '8px' }}>
            📊 StatTrak™ Stats
          </div>
          {(statTrakTooltip.item.trackedStats || []).map(stat => {
            const value = statTrakTooltip.item.statValues?.[stat] || 0;
            const label = stat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            return (
              <div key={stat} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', fontSize: '12px', marginBottom: '4px' }}>
                <span style={{ color: '#aaa' }}>{label}</span>
                <span style={{ color: '#FF8C00', fontWeight: 'bold' }}>{value}</span>
              </div>
            );
          })}
          {(!statTrakTooltip.item.trackedStats || statTrakTooltip.item.trackedStats.length === 0) && (
            <div style={{ color: '#666', fontSize: '12px' }}>No stats tracked yet</div>
          )}
        </div>
      )}
      {vfxTooltip && (
        <div style={{
          position: 'fixed',
          left: vfxTooltip.x + 14,
          top: vfxTooltip.y + (statTrakTooltip ? 120 : 14),
          zIndex: 9999,
          background: 'rgba(20,20,20,0.97)',
          border: '2px solid #a78bfa',
          borderRadius: '10px',
          padding: '12px 16px',
          pointerEvents: 'none',
          minWidth: '160px',
          boxShadow: '0 4px 24px rgba(167,139,250,0.3)'
        }}>
          <div style={{ color: '#a78bfa', fontWeight: 'bold', fontSize: '13px', marginBottom: '8px' }}>✦ VFX Effects</div>
          {(Array.isArray(vfxTooltip.item.vfx) ? vfxTooltip.item.vfx : [vfxTooltip.item.vfx]).map((effectId, idx) => {
            const effect = VFX_EFFECTS.find(e => e.id === effectId);
            return (
              <div key={idx} style={{ fontSize: '12px', marginBottom: '4px', color: '#c4b5fd' }}>
                <span style={{ fontWeight: 'bold' }}>{effect ? effect.name : effectId}</span>
                {effect && <span style={{ color: '#888', marginLeft: '6px' }}>— {effect.description}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TradeUpSection;
